import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import styles from './CommandeDetail.module.css';

interface CommandeDetail {
  id: number;
  nomClient: string;
  prenomClient: string;
  emailClient: string;
  total: number;
  subtotal: number;
  fraisLivraison: number;
  reductionsJson: string;
  modeLivraison: string;
  methodePaiement: string;
  statutPaiement: string;
  statutLivraison: string;
  pointRelaisNum: string | null;
  clientTelephone: string | null;
  clientAdresse: string | null;
  clientCP: string | null;
  clientVille: string | null;
  dateCommande: string;
}

interface Produit {
  id: number;
  name: string;
  quantite: number;
  prixUnitaire: number;
}

const STATUT_PAIEMENT_OPTIONS = [
  { value: 'en_attente', label: 'En attente' },
  { value: 'paye', label: 'Payé' },
  { value: 'echec', label: 'Échec' },
  { value: 'rembourse', label: 'Remboursé' },
];

const STATUT_LIVRAISON_OPTIONS = [
  { value: 'en_attente', label: 'En attente' },
  { value: 'en_preparation', label: 'En préparation' },
  { value: 'depose_transporteur', label: 'Déposé transporteur' },
  { value: 'en_transit', label: 'En transit' },
  { value: 'disponible_point_relais', label: 'Dispo point relais' },
  { value: 'livre', label: 'Livré' },
];

// ── Infos expéditeur (modifiez selon vos coordonnées) ──────────────
const EXPEDITEUR = {
  nom: 'E Liquide Sud',
  adresse: '123 rue de la Vape',
  codePostal: '13000',
  ville: 'Marseille',
  email: 'contact@eliquidesud.fr',
  tel: '06 00 00 00 00',
};
// ────────────────────────────────────────────────────────────────────

export const CommandeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [commande, setCommande] = useState<CommandeDetail | null>(null);
  const [produits, setProduits] = useState<Produit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checked, setChecked] = useState<Set<number>>(new Set());

  // Mondial Relay
  const [showMRModal, setShowMRModal]       = useState(false);
  const [mrTelephone, setMrTelephone]       = useState('');
  const [mrRelayNum, setMrRelayNum]         = useState('');
  const [mrPoids, setMrPoids]               = useState(500);
  const [mrDestCP, setMrDestCP]             = useState('');
  const [mrDestVille, setMrDestVille]       = useState('');
  const [mrDestAdresse, setMrDestAdresse]   = useState('');
  const [mrLoading, setMrLoading]           = useState(false);
  const [mrError, setMrError]               = useState('');
  const [debugResults, setDebugResults]     = useState<any[] | null>(null);
  const [debugLoading, setDebugLoading]     = useState(false);

  useEffect(() => {
    if (id) loadCommande(Number(id));
  }, [id]);

  const loadCommande = async (commandeId: number) => {
    try {
      setLoading(true);
      setError('');
      const [resCommande, resProduits] = await Promise.all([
        api.get<CommandeDetail>(`commandes/${commandeId}`),
        api.get<Produit[]>(`commandes/${commandeId}/produits`),
      ]);
      setCommande(resCommande.data);
      setProduits(resProduits.data ?? []);
    } catch (err: any) {
      const apiError = err.response?.data?.error || err.response?.data?.message || '';
      setError('Erreur : ' + (apiError || err.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (field: 'statutPaiement' | 'statutLivraison', value: string) => {
    if (!commande) return;
    try {
      await api.put(`commandes/${commande.id}/status`, { [field]: value });
      setCommande((prev) => prev ? { ...prev, [field]: value } : prev);
    } catch (err: any) {
      alert('Erreur mise à jour : ' + (err.response?.data?.message || err.message));
    }
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n ?? 0);

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const fmtDateShort = (d: string) =>
    new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });

  const getBadge = (type: 'paiement' | 'livraison', statut: string) => {
    const mapPaiement: Record<string, { label: string; cls: string }> = {
      paye:      { label: '✓ Payé',       cls: styles.green },
      en_attente:{ label: '⏳ En attente', cls: styles.orange },
      echec:     { label: '✕ Échec',      cls: styles.red },
      rembourse: { label: '↩ Remboursé',  cls: styles.blue },
    };
    const mapLivraison: Record<string, { label: string; cls: string }> = {
      livre:                  { label: '✓ Livré',          cls: styles.green },
      en_preparation:         { label: '📦 En préparation', cls: styles.purple },
      en_attente:             { label: 'En attente',        cls: styles.gray },
      depose_transporteur:    { label: '🚚 Déposé',         cls: styles.blue },
      en_transit:             { label: '🚚 En transit',     cls: styles.blue },
      disponible_point_relais:{ label: '📍 Dispo relais',   cls: styles.teal },
    };
    const map = type === 'paiement' ? mapPaiement : mapLivraison;
    const info = map[statut] || { label: statut, cls: '' };
    return <span className={`${styles.badge} ${info.cls}`}>{info.label}</span>;
  };

  const genererEtiquetteMR = async () => {
    if (!commande) return;
    if (!mrTelephone.trim())   { setMrError('Le téléphone est requis.'); return; }
    if (!mrRelayNum.trim())    { setMrError('Le numéro de point relais est requis.'); return; }
    if (!mrDestCP.trim())      { setMrError('Le code postal du destinataire est requis.'); return; }
    if (!mrDestVille.trim())   { setMrError('La ville du destinataire est requise.'); return; }
    try {
      setMrLoading(true);
      setMrError('');
      const res = await api.post<{ numExpedition: string; urlEtiquette: string }>(
        `commandes/${commande.id}/etiquette-mondial-relay`,
        { telephone: mrTelephone, pointRelaisNum: mrRelayNum, poidsGrammes: mrPoids, destCP: mrDestCP, destVille: mrDestVille, destAdresse: mrDestAdresse }
      );
      const { urlEtiquette, numExpedition } = res.data;
      setShowMRModal(false);
      window.open(urlEtiquette, '_blank');
      alert(`Étiquette générée !\nN° expédition : ${numExpedition}`);
    } catch (err: any) {
      const errData = err.response?.data;
      const msg = errData?.error || err.message || 'Erreur inconnue';

      // ══ TOUT AFFICHER DANS LA CONSOLE NAVIGATEUR (F12 > Console) ══
      console.error('%c═══ MONDIAL RELAY ERREUR ═══', 'color:red;font-weight:bold;font-size:14px');
      console.error('Message :', msg);
      if (errData?.debug) {
        console.error('Données envoyées à Mondial Relay :', errData.debug);
        console.error('SecStr (clé masquée) :', errData.debug.secStrMasked);
        console.error('Hash MD5 calculé     :', errData.debug.security);
      }
      if (errData?.raw) console.error('Réponse XML brute :', errData.raw);
      console.error('Réponse complète :', errData);
      // ═════════════════════════════════════════════════════════════

      setMrError(msg + '\n\n👉 Ouvrez F12 > Console pour voir tous les détails');
    } finally {
      setMrLoading(false);
    }
  };

  const debugMR = async () => {
    try {
      setDebugLoading(true);
      setDebugResults(null);
      const res = await api.get<any[]>('commandes/debug-mr', { params: { relayNum: commande?.pointRelaisNum || '024168' } });
      setDebugResults(res.data);
    } catch (err: any) {
      setDebugResults([{ label: 'Erreur', error: err.response?.data || err.message }]);
    } finally {
      setDebugLoading(false);
    }
  };

  const toggleCheck = (index: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.loading}>Chargement de la commande...</div>
        </div>
      </Layout>
    );
  }

  if (error || !commande) {
    return (
      <Layout>
        <div className={styles.container}>
          <button className={styles.btnBack} onClick={() => navigate('/commandes')}>
            ← Retour aux commandes
          </button>
          <div className={styles.error}>{error || 'Commande introuvable.'}</div>
        </div>
      </Layout>
    );
  }

  const totalProduits = produits.reduce((sum, p) => sum + p.quantite * p.prixUnitaire, 0);

  return (
    <Layout>
      <div className={styles.container}>

        {/* ═══ MODAL MONDIAL RELAY ═══ */}
        {showMRModal && (
          <div className={styles.modalOverlay} onClick={() => setShowMRModal(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>📦 Générer étiquette Mondial Relay</h2>
                <button className={styles.modalClose} onClick={() => setShowMRModal(false)}>✕</button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.modalInfo}>
                  <strong>{commande.prenomClient} {commande.nomClient}</strong>
                  <span> — Point relais</span>
                  {commande.pointRelaisNum && <span> #{commande.pointRelaisNum}</span>}
                </div>

                <label className={styles.modalLabel}>
                  Téléphone du client <span className={styles.required}>*</span>
                  <input
                    type="tel"
                    className={styles.modalInput}
                    placeholder="ex : 0612345678"
                    value={mrTelephone}
                    onChange={(e) => setMrTelephone(e.target.value)}
                  />
                </label>

                <label className={styles.modalLabel}>
                  Numéro du point relais <span className={styles.required}>*</span>
                  <input
                    type="text"
                    className={styles.modalInput}
                    placeholder="ex : 003007"
                    value={mrRelayNum}
                    onChange={(e) => setMrRelayNum(e.target.value)}
                  />
                </label>

                <label className={styles.modalLabel}>
                  Adresse du destinataire (optionnel)
                  <input
                    type="text"
                    className={styles.modalInput}
                    placeholder="ex : 12 rue des Fleurs"
                    value={mrDestAdresse}
                    onChange={(e) => setMrDestAdresse(e.target.value)}
                  />
                </label>

                <label className={styles.modalLabel}>
                  Code postal destinataire <span className={styles.required}>*</span>
                  <input
                    type="text"
                    className={styles.modalInput}
                    placeholder="ex : 13000"
                    value={mrDestCP}
                    onChange={(e) => setMrDestCP(e.target.value)}
                  />
                </label>

                <label className={styles.modalLabel}>
                  Ville destinataire <span className={styles.required}>*</span>
                  <input
                    type="text"
                    className={styles.modalInput}
                    placeholder="ex : MARSEILLE"
                    value={mrDestVille}
                    onChange={(e) => setMrDestVille(e.target.value)}
                  />
                </label>

                <label className={styles.modalLabel}>
                  Poids du colis (en grammes)
                  <input
                    type="number"
                    className={styles.modalInput}
                    min={1}
                    max={30000}
                    value={mrPoids}
                    onChange={(e) => setMrPoids(Number(e.target.value))}
                  />
                </label>

                {mrError && <div className={styles.modalError}>{mrError}</div>}
              </div>

              <div className={styles.modalFooter}>
                <button className={styles.btnCancel} onClick={() => setShowMRModal(false)}>
                  Annuler
                </button>
                <button
                  className={styles.btnGenerate}
                  onClick={genererEtiquetteMR}
                  disabled={mrLoading}
                >
                  {mrLoading ? 'Génération...' : '📦 Générer l\'étiquette'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ═══ FIN MODAL ═══ */}

        {/* ═══ BORDEREAU D'IMPRESSION (masqué à l'écran, visible à l'impression) ═══ */}
        <div className={styles.bordereau}>
          <div className={styles.bordereauHeader}>
            <div className={styles.bordereauExpéditeur}>
              <div className={styles.bordereauShopName}>{EXPEDITEUR.nom}</div>
              <div>{EXPEDITEUR.adresse}</div>
              <div>{EXPEDITEUR.codePostal} {EXPEDITEUR.ville}</div>
              <div>{EXPEDITEUR.email}</div>
              <div>{EXPEDITEUR.tel}</div>
            </div>
            <div className={styles.bordereauMeta}>
              <div className={styles.bordereauNumero}>Commande #{commande.id}</div>
              <div className={styles.bordereauDate}>Date : {fmtDateShort(commande.dateCommande)}</div>
              <div className={styles.bordereauMode}>
                {commande.modeLivraison === 'mondial_relay' ? 'Mondial Relay' : 'Livraison domicile'}
                {commande.pointRelaisNum && ` — Point relais #${commande.pointRelaisNum}`}
              </div>
            </div>
          </div>

          <div className={styles.bordereauDestinataire}>
            <div className={styles.bordereauDestLabel}>DESTINATAIRE</div>
            <div className={styles.bordereauDestName}>{commande.prenomClient} {commande.nomClient}</div>
            <div className={styles.bordereauDestEmail}>{commande.emailClient}</div>
          </div>

          <table className={styles.bordereauTable}>
            <thead>
              <tr>
                <th>Produit</th>
                <th>Qté</th>
                <th>Prix unit.</th>
                <th>Sous-total</th>
              </tr>
            </thead>
            <tbody>
              {produits.map((p, i) => (
                <tr key={i}>
                  <td>{p.name}</td>
                  <td style={{ textAlign: 'center' }}>{p.quantite}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(p.prixUnitaire)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(p.quantite * p.prixUnitaire)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {commande.fraisLivraison > 0 && (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'right' }}>Frais de livraison</td>
                  <td style={{ textAlign: 'right' }}>{fmt(commande.fraisLivraison)}</td>
                </tr>
              )}
              <tr className={styles.bordereauTotal}>
                <td colSpan={3} style={{ textAlign: 'right' }}>TOTAL</td>
                <td style={{ textAlign: 'right' }}>{fmt(commande.total)}</td>
              </tr>
            </tfoot>
          </table>

          <div className={styles.bordereauFooter}>
            Merci pour votre commande ! — {EXPEDITEUR.nom} — {EXPEDITEUR.email}
          </div>
        </div>
        {/* ═══ FIN BORDEREAU ═══ */}

        {/* Header */}
        <div className={styles.header}>
          <button className={styles.btnBack} onClick={() => navigate('/commandes')}>
            ← Retour aux commandes
          </button>
          <h1 className={styles.title}>Commande #{commande.id}</h1>
          <span className={styles.date}>{fmtDate(commande.dateCommande)}</span>
          <button className={styles.btnPrint} onClick={() => window.print()}>
            🖨️ Imprimer le bordereau
          </button>
          {commande.modeLivraison === 'mondial_relay' && (
            <button
              className={styles.btnMR}
              onClick={() => {
                setMrRelayNum(commande.pointRelaisNum ?? '');
                setMrTelephone(commande.clientTelephone ?? '');
                setMrDestAdresse(commande.clientAdresse ?? '');
                setMrDestCP(commande.clientCP ?? '');
                setMrDestVille(commande.clientVille ?? '');
                setMrError('');
                setShowMRModal(true);
              }}
            >
              📦 Étiquette Mondial Relay
            </button>
          )}
          <button
            style={{ background:'#6c757d', color:'#fff', border:'none', borderRadius:'6px', padding:'8px 14px', cursor:'pointer', fontSize:'13px' }}
            onClick={debugMR}
            disabled={debugLoading}
          >
            {debugLoading ? '⏳ Test...' : '🔍 Debug MR'}
          </button>
        </div>

        <div className={styles.grid}>
          {/* Infos client */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Client</h2>
            <div className={styles.infoRow}>
              <span className={styles.label}>Nom</span>
              <span>{commande.prenomClient} {commande.nomClient}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Email</span>
              <a href={`mailto:${commande.emailClient}`} className={styles.emailLink}>
                {commande.emailClient}
              </a>
            </div>
          </div>

          {/* Livraison */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Livraison</h2>
            <div className={styles.infoRow}>
              <span className={styles.label}>Mode</span>
              <span>{commande.modeLivraison === 'mondial_relay' ? '📦 Mondial Relay' : '🏠 Domicile'}</span>
            </div>
            {commande.pointRelaisNum && (
              <div className={styles.infoRow}>
                <span className={styles.label}>Point relais</span>
                <span>#{commande.pointRelaisNum}</span>
              </div>
            )}
            <div className={styles.infoRow}>
              <span className={styles.label}>Statut</span>
              <div className={styles.statusRow}>
                {getBadge('livraison', commande.statutLivraison)}
                <select
                  value={commande.statutLivraison}
                  onChange={(e) => handleStatusChange('statutLivraison', e.target.value)}
                  className={styles.select}
                >
                  {STATUT_LIVRAISON_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Paiement */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Paiement</h2>
            <div className={styles.infoRow}>
              <span className={styles.label}>Méthode</span>
              <span>{commande.methodePaiement || '—'}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Statut</span>
              <div className={styles.statusRow}>
                {getBadge('paiement', commande.statutPaiement)}
                <select
                  value={commande.statutPaiement}
                  onChange={(e) => handleStatusChange('statutPaiement', e.target.value)}
                  className={styles.select}
                >
                  {STATUT_PAIEMENT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Montants */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Montants</h2>
            <div className={styles.infoRow}>
              <span className={styles.label}>Sous-total</span>
              <span>{fmt(commande.subtotal)}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Frais livraison</span>
              <span>{fmt(commande.fraisLivraison)}</span>
            </div>
            <div className={`${styles.infoRow} ${styles.totalRow}`}>
              <span className={styles.label}>Total</span>
              <strong>{fmt(commande.total)}</strong>
            </div>
          </div>
        </div>

        {/* Produits commandés */}
        <div className={styles.productsCard}>
          <div className={styles.productsHeader}>
            <h2 className={styles.cardTitle} style={{ margin: 0 }}>Produits commandés</h2>
            {produits.length > 0 && (
              <span className={`${styles.progressBadge} ${checked.size === produits.length ? styles.progressDone : ''}`}>
                {checked.size === produits.length
                  ? '✓ Tous préparés !'
                  : `${checked.size} / ${produits.length} préparé${checked.size > 1 ? 's' : ''}`}
              </span>
            )}
          </div>
          {produits.length === 0 ? (
            <p className={styles.noData}>Aucun produit trouvé pour cette commande.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.center}>✓</th>
                  <th>Produit</th>
                  <th className={styles.center}>Quantité</th>
                  <th className={styles.center}>Prix unitaire</th>
                  <th className={styles.center}>Sous-total</th>
                </tr>
              </thead>
              <tbody>
                {produits.map((p, i) => (
                  <tr
                    key={i}
                    className={checked.has(i) ? styles.rowChecked : ''}
                    onClick={() => toggleCheck(i)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className={styles.center}>
                      <input
                        type="checkbox"
                        checked={checked.has(i)}
                        onChange={() => toggleCheck(i)}
                        onClick={(e) => e.stopPropagation()}
                        className={styles.checkbox}
                      />
                    </td>
                    <td className={`${styles.productName} ${checked.has(i) ? styles.productChecked : ''}`}>
                      {p.name}
                    </td>
                    <td className={styles.center}>{p.quantite}</td>
                    <td className={styles.center}>{fmt(p.prixUnitaire)}</td>
                    <td className={styles.center}><strong>{fmt(p.quantite * p.prixUnitaire)}</strong></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} className={styles.footLabel}>Total produits</td>
                  <td className={styles.center}><strong>{fmt(totalProduits)}</strong></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
        {/* ═══ DEBUG MONDIAL RELAY ═══ */}
        {debugResults && (
          <div style={{ margin:'24px 0', padding:'16px', background:'#1e1e2e', borderRadius:'8px', color:'#cdd6f4', fontFamily:'monospace', fontSize:'13px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'12px' }}>
              <strong style={{ color:'#cba6f7' }}>🔍 Résultats diagnostic Mondial Relay</strong>
              <button onClick={() => setDebugResults(null)} style={{ background:'none', border:'none', color:'#f38ba8', cursor:'pointer', fontSize:'16px' }}>✕</button>
            </div>
            {debugResults.map((r, i) => (
              <div key={i} style={{ marginBottom:'10px', padding:'10px', background:'#313244', borderRadius:'6px', borderLeft:`4px solid ${r.ok ? '#a6e3a1' : r.stat !== '33' ? '#fab387' : '#f38ba8'}` }}>
                <div style={{ fontWeight:'bold', marginBottom:'4px', color: r.ok ? '#a6e3a1' : r.stat !== '33' ? '#fab387' : '#f38ba8' }}>
                  {r.label} → STAT: {r.stat ?? '?'} {r.ok ? '✅ PASSÉ !' : '❌'}
                  {r.statLabel && !r.ok && <span style={{ fontWeight:'normal', marginLeft:'6px', color:'#a6adc8' }}>({r.statLabel})</span>}
                  {r.error && <span style={{ fontWeight:'normal', marginLeft:'6px' }}>{JSON.stringify(r.error)}</span>}
                </div>
                <div style={{ color:'#a6adc8', fontSize:'11px' }}>
                  Ad1: "<em>{r.expeAd1 || ''}</em>" | Ad2: "<em>{r.expeAd2 || ''}</em>" | Ad3: "<em>{r.expeAd3 || ''}</em>" | Ad4: "<em>{r.expeAd4 || ''}</em>" | Ville: "<em>{r.ville || ''}</em>" | CP: "<em>{r.cp || ''}</em>"
                </div>
              </div>
            ))}
            <div style={{ marginTop:'10px', color:'#6c7086', fontSize:'11px' }}>
              T1-T3 = TEST (TTMRSDBX) | T4-T10 = PROD (CC23EC19).<br/>
              🔬 Si T6 (Ville="PARIS FAUX") ou T7 (CP="99999") changent de stat → Ad1..Ad4 config sont corrects !
            </div>
          </div>
        )}
        {/* ═══ FIN DEBUG ═══ */}

      </div>
    </Layout>
  );
};
